// src/pages/billing/PricingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

const FEATURES_FREE = [
    'Resume builder',
    'Job board access',
    '2 AI optimizations per day',
];

const FEATURES_PREMIUM = [
    'Everything in Free',
    'Unlimited AI optimizations',
    'Resume PDF download',
    'Priority support',
];

const PricingPage = () => {
    const { isPremium } = useAuth();
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
    const [couponCode, setCouponCode] = useState('');
    const [couponStatus, setCouponStatus] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [prices, setPrices] = useState(null);

    const debounceRef = useRef(null);

    // Live prices from Stripe
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/api/get_prices.php`, { credentials: 'include' });
                const data = await res.json();
                if (data.status === 'success') setPrices(data);
            } catch (_) { /* fall back to plan name */ }
        })();
    }, []);

    // Debounced coupon validation
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!couponCode.trim()) { setCouponStatus(null); return; }
        debounceRef.current = setTimeout(async () => {
            setCouponLoading(true);
            try {
                const response = await fetch(`${API}/api/validate_coupon.php`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ code: couponCode.trim(), plan: billingCycle }),
                });
                const result = await response.json();
                setCouponStatus(result.status === 'success' ? result : { valid: false, reason: 'Unable to validate coupon.' });
            } catch (error) {
                setCouponStatus({ valid: false, reason: 'Network error. Please try again.' });
            } finally {
                setCouponLoading(false);
            }
        }, 500);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [couponCode, billingCycle]);

    const handleUpgrade = async () => {
        setMessage({ type: '', text: '' });
        setCheckoutLoading(true);
        try {
            const body = { plan: billingCycle };
            if (couponCode.trim() && couponStatus?.valid) body.code = couponCode.trim();
            const response = await fetch(`${API}/api/create_checkout_session.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(body),
            });
            const result = await response.json();
            if (response.ok && result.status === 'success' && result.url) {
                window.location = result.url;
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed to start checkout. Please try again.' });
                setCheckoutLoading(false);
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'An error occurred. Please try again later.' });
            setCheckoutLoading(false);
        }
    };

    const formatPrice = (p) => {
        if (!p || p.amount == null) return null;
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: (p.currency || 'usd').toUpperCase() }).format(p.amount / 100);
    };

    const getDiscountedAmount = (p) => {
        if (!p || p.amount == null || !couponStatus?.valid) return null;
        const { discount_type, discount_value } = couponStatus;
        if (discount_type === 'percent') return Math.round(p.amount * (1 - discount_value / 100));
        if (discount_type === 'amount') return Math.max(0, p.amount - Math.round(discount_value * 100));
        return null;
    };

    const formatCouponLabel = () => {
        if (!couponStatus?.valid) return null;
        const { discount_type, discount_value } = couponStatus;
        if (discount_type === 'percent') return `${discount_value}% off`;
        if (discount_type === 'amount') return `$${discount_value} off`;
        return 'Discount applied';
    };

    // Annual savings vs paying monthly × 12
    const annualFullYear = prices?.monthly?.amount ? prices.monthly.amount * 12 : null;
    const annualSavingsPct = (annualFullYear && prices?.annual?.amount && prices.annual.amount < annualFullYear)
        ? Math.round((1 - prices.annual.amount / annualFullYear) * 100)
        : null;

    const FeatureItem = ({ text }) => (
        <li className="flex items-start gap-2.5 text-sm text-neutral-300">
            <CheckCircleIcon className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <span>{text}</span>
        </li>
    );

    const renderPrice = () => {
        const priceObj = billingCycle === 'monthly' ? prices?.monthly : prices?.annual;
        const base = formatPrice(priceObj);
        if (!base) return <span className="text-4xl font-bold">{billingCycle === 'monthly' ? 'Monthly' : 'Annual'}</span>;
        const suffix = <span className="text-base font-normal text-neutral-400">{billingCycle === 'monthly' ? '/mo' : '/yr'}</span>;
        const discCents = getDiscountedAmount(priceObj);
        const discounted = discCents != null ? formatPrice({ amount: discCents, currency: priceObj.currency }) : null;
        const annualList = (billingCycle === 'annual' && annualFullYear && priceObj.amount < annualFullYear)
            ? formatPrice({ amount: annualFullYear, currency: priceObj.currency }) : null;

        if (discounted) {
            return (<><span className="text-2xl text-neutral-500 line-through mr-2">{base}</span><span className="text-4xl font-bold text-cyan-300">{discounted}</span>{suffix}</>);
        }
        return (
            <>
                {annualList && <span className="text-2xl text-neutral-500 line-through mr-2">{annualList}</span>}
                <span className="text-4xl font-bold">{base}</span>{suffix}
            </>
        );
    };

    return (
        <div className="px-4 py-12 sm:py-16 text-white">
            <div className="max-w-3xl mx-auto">
                <header className="text-center mb-8">
                    <h1 className="text-4xl font-bold tracking-tight mb-2">Choose your plan</h1>
                    <p className="text-neutral-400">Unlock unlimited optimizations and polished PDF downloads.</p>
                </header>

                {/* Billing cycle toggle */}
                <div className="flex items-center justify-center gap-3 mb-10">
                    <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-neutral-500'}`}>Monthly</span>
                    <button
                        onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingCycle === 'annual' ? 'bg-cyan-500' : 'bg-neutral-700'}`}
                        aria-label="Toggle billing cycle"
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className={`text-sm font-medium flex items-center gap-2 ${billingCycle === 'annual' ? 'text-white' : 'text-neutral-500'}`}>
                        Annual
                        {annualSavingsPct && (
                            <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 px-2 py-0.5 rounded-full">
                                Save {annualSavingsPct}%
                            </span>
                        )}
                    </span>
                </div>

                {/* Plan cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Free */}
                    <div className="rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 flex flex-col">
                        <h2 className="text-lg font-bold mb-1">Free</h2>
                        <p className="text-4xl font-bold mb-1">$0</p>
                        <p className="text-sm text-neutral-500 mb-6">Forever free</p>
                        <ul className="space-y-3 mb-6 flex-1">
                            {FEATURES_FREE.map(f => <FeatureItem key={f} text={f} />)}
                        </ul>
                        <button disabled className="w-full border border-white/10 text-neutral-400 font-semibold py-2.5 px-4 rounded-lg cursor-default text-sm">
                            {isPremium ? 'Included with Premium' : 'Current plan'}
                        </button>
                    </div>

                    {/* Premium */}
                    <div className="rounded-2xl border border-cyan-500/40 bg-cyan-500/[0.06] p-6 relative flex flex-col shadow-xl shadow-cyan-500/5">
                        <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full ${isPremium ? 'bg-cyan-500 text-black' : 'bg-cyan-500 text-black'}`}>
                            {isPremium ? 'Your plan' : 'Recommended'}
                        </span>
                        <h2 className="text-lg font-bold mb-1">Premium</h2>
                        <p className="mb-1">{renderPrice()}</p>
                        <p className="text-sm text-neutral-500 mb-6">
                            {billingCycle === 'monthly' ? 'Billed monthly · cancel anytime' : 'One-time payment · 1 year of access'}
                        </p>
                        <ul className="space-y-3 mb-6 flex-1">
                            {FEATURES_PREMIUM.map(f => <FeatureItem key={f} text={f} />)}
                        </ul>

                        {isPremium ? (
                            <div>
                                <div className="w-full flex items-center justify-center gap-2 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 font-semibold py-2.5 px-4 rounded-lg text-sm">
                                    <CheckCircleIcon className="h-5 w-5" /> Your current plan
                                </div>
                                <Link to="/settings/profile" className="block text-center text-xs text-neutral-400 hover:text-white mt-3 underline">
                                    Manage or cancel in your profile
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        placeholder="Coupon code (optional)"
                                        className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent placeholder-neutral-600"
                                    />
                                    <div className="mt-1.5 min-h-[1.1rem]">
                                        {couponLoading && <p className="text-xs text-neutral-500">Validating…</p>}
                                        {!couponLoading && couponStatus !== null && (
                                            couponStatus.valid
                                                ? <p className="text-xs text-emerald-400 font-medium">✓ {formatCouponLabel()}</p>
                                                : <p className="text-xs text-red-400">{couponStatus.reason || 'Invalid code'}</p>
                                        )}
                                    </div>
                                </div>
                                <button onClick={handleUpgrade} disabled={checkoutLoading}
                                    className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2.5 px-4 rounded-lg disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/20">
                                    {checkoutLoading ? 'Redirecting…' : `Upgrade — ${billingCycle === 'monthly' ? 'Monthly' : 'Annual'}`}
                                </button>
                                {message.text && (
                                    <p className={`text-sm text-center mt-3 ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message.text}</p>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <p className="text-center text-xs text-neutral-500 mt-8">
                    Secure checkout powered by Stripe. Cancel anytime.
                </p>
            </div>
        </div>
    );
};

export default PricingPage;
